import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
// A DLS não tem ícones equivalentes a ClipboardList e UserCog, por isso o import direto do lucide-react
import { ClipboardList, UserCog } from 'lucide-react'
import {
  AppShell,
  Avatar,
  Icon,
  Sidebar,
  SidebarDrawer,
  Topbar,
  useToast,
} from '@navegasistemas/dls-navega-rjs'
import { useMutation } from '@tanstack/react-query'
import { authService } from '@/features/auth/services/auth.service'
import { useAuthStore } from '@/store/auth-store'
import { useAuth } from '@/hooks/use-auth'
import { PERMISSIONS } from '@/lib/permissions'
import { navigationGuard } from '@/lib/navigation-guard'
import { useExportEvents } from '@/features/file-exports/hooks/use-export-events'
import { ExportsPopover } from '@/features/file-exports/components/exports-popover'

const MOBILE_BP = 768
const TABLET_BP = 1024

function getScreenClass() {
  const w = typeof window !== 'undefined' ? window.innerWidth : TABLET_BP
  if (w < MOBILE_BP) return 'mobile' as const
  if (w < TABLET_BP) return 'tablet' as const
  return 'desktop' as const
}

const NAV_GROUPS: {
  label: string
  items: { key: string; label: string; icon: React.ReactNode; permission?: string }[]
}[] = [
  {
    label: 'Gestão',
    items: [
      { key: '/users', label: 'Usuários', icon: <UserCog />, permission: PERMISSIONS.USER_LIST },
      {
        key: '/agendas',
        label: 'Agenda Gerencial',
        icon: <Icon name="calendar" />,
        permission: PERMISSIONS.AGENDA_READ,
      },
      {
        key: '/agendas/dashboard',
        label: 'Dashboard Gerencial',
        icon: <Icon name="bar-chart" />,
        permission: PERMISSIONS.AGENDA_DASHBOARD,
      },
      {
        key: '/users/audit',
        label: 'Auditoria',
        icon: <ClipboardList />,
        permission: PERMISSIONS.AUDIT_READ,
      },
      {
        key: '/roles',
        label: 'Perfis de acesso',
        icon: <Icon name="shield-check" />,
        permission: PERMISSIONS.ROLE_READ,
      },
    ],
  },
  {
    label: 'Análise',
    items: [
      {
        key: '/manager-panel',
        label: 'Painel do Gerente',
        icon: <Icon name="pie-chart" />,
        permission: PERMISSIONS.MANAGER_PANEL_READ,
      },
      {
        key: '/associates',
        label: 'Painel do Associado',
        icon: <Icon name="users" />,
        permission: PERMISSIONS.ASSOCIATE_PANEL_READ,
      },
      {
        key: '/accounts',
        label: 'Contas gerenciais',
        icon: <Icon name="bar-chart" />,
        permission: PERMISSIONS.ACCOUNT_LIST,
      },
      {
        key: '/accounts/custom-accounts',
        label: 'Contas personalizadas',
        icon: <Icon name="wallet" />,
        permission: PERMISSIONS.ACCOUNT_CUSTOM_ACCOUNT_LIST,
      },
      {
        key: '/exportacoes',
        label: 'Exportações',
        icon: <Icon name="download" />,
      },
      {
        key: '/goals',
        label: 'Gerenciamento de Metas',
        icon: <Icon name="trending-up" />,
        permission: PERMISSIONS.GOAL_LIST,
      },
      {
        key: '/goals/tracking',
        label: 'Acompanhamento de Metas',
        icon: <Icon name="bar-chart" />,
        permission: PERMISSIONS.GOAL_TRACK,
      },
      {
        key: '/relatorios',
        label: 'Relatórios',
        icon: <Icon name="file-text" />,
      },
    ],
  },
  {
    label: 'Configurações',
    items: [
      {
        key: '/parameters',
        label: 'Parâmetros',
        icon: <Icon name="sliders" />,
        permission: PERMISSIONS.PARAMETERS_READ,
      },
    ],
  },
]

const BREADCRUMB_LABELS: Record<string, string> = {
  '/roles': 'Perfis de acesso',
  '/users': 'Usuários',
  '/users/new': 'Novo usuário',
  '/users/import': 'Importação em lote',
  '/users/audit': 'Auditoria',
  '/preferences': 'Preferências',
  '/parameters': 'Parâmetros',
  '/accounts': 'Contas gerenciais',
  '/accounts/groups': 'Grupos de análise',
  '/accounts/custom-accounts': 'Contas personalizadas',
  '/accounts/custom-accounts/import': 'Importar saldos',
  '/exportacoes': 'Exportações',
  '/agendas': 'Agenda Gerencial',
  '/agendas/dashboard': 'Dashboard Gerencial',
  '/agendas/detail': 'Detalhe',
  '/goals': 'Gerenciamento de Metas',
  '/goals/new': 'Nova meta',
  '/goals/tracking': 'Acompanhamento de Metas',
  '/relatorios': 'Relatórios',
  '/associates': 'Painel do Associado',
  '/manager-panel': 'Painel do Gerente',
}

function getBreadcrumbItems(pathname: string): { label: string }[] {
  if (/^\/users\/.+/.test(pathname)) {
    return [{ label: 'Usuários' }, { label: BREADCRUMB_LABELS[pathname] ?? 'Detalhes' }]
  }
  if (/^\/roles\/.+/.test(pathname)) {
    return [{ label: 'Perfis de acesso' }, { label: BREADCRUMB_LABELS[pathname] ?? 'Detalhes' }]
  }
  if (pathname === '/accounts/groups') {
    return [{ label: 'Contas gerenciais' }, { label: 'Grupos de análise' }]
  }
  if (pathname === '/accounts/custom-accounts') {
    return [{ label: 'Contas gerenciais' }, { label: 'Contas personalizadas' }]
  }
  if (pathname === '/accounts/custom-accounts/import') {
    return [
      { label: 'Contas gerenciais' },
      { label: 'Contas personalizadas' },
      { label: 'Importar saldos' },
    ]
  }
  if (/^\/agendas\/.+/.test(pathname)) {
    return [{ label: 'Agenda Gerencial' }, { label: 'Detalhe' }]
  }
  if (pathname === '/goals/new') {
    return [{ label: 'Gerenciamento de Metas' }, { label: 'Nova meta' }]
  }
  if (/^\/goals\/.+\/edit$/.test(pathname)) {
    return [{ label: 'Gerenciamento de Metas' }, { label: 'Editar meta' }]
  }
  if (/^\/goals\/tracking\/.+/.test(pathname)) {
    return [{ label: 'Acompanhamento de Metas' }, { label: 'Detalhe do item' }]
  }
  if (/^\/relatorios\/.+/.test(pathname)) {
    return [{ label: 'Relatórios' }, { label: 'Detalhe' }]
  }
  if (/^\/manager-panel\/.+/.test(pathname)) {
    return [{ label: 'Painel do Gerente' }, { label: 'Detalhe' }]
  }
  if (/^\/associates\/.+/.test(pathname)) {
    return [{ label: 'Painel do Associado' }, { label: 'Detalhe' }]
  }
  return [{ label: BREADCRUMB_LABELS[pathname] ?? 'Início' }]
}

function getActiveKey(pathname: string): string {
  if (pathname.startsWith('/users/')) {
    if (pathname === '/users/audit') return '/users/audit'
    return '/users'
  }
  if (pathname.startsWith('/accounts/custom-accounts')) return '/accounts/custom-accounts'
  if (pathname.startsWith('/accounts')) return '/accounts'
  if (pathname === '/agendas/dashboard') return '/agendas/dashboard'
  if (pathname.startsWith('/agendas/')) return '/agendas'
  if (pathname.startsWith('/goals/tracking')) return '/goals/tracking'
  if (pathname.startsWith('/goals')) return '/goals'
  if (pathname.startsWith('/relatorios')) return '/relatorios'
  if (pathname.startsWith('/manager-panel')) return '/manager-panel'
  if (pathname.startsWith('/associates')) return '/associates'
  return pathname
}

export function AppLayout() {
  const [screen, setScreen] = useState(getScreenClass)
  const [collapsed, setCollapsed] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useExportEvents()

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { toast } = useToast()
  const { accessToken, refreshToken, clearAuth } = useAuthStore()
  const { user, can } = useAuth()

  useEffect(() => {
    function handleResize() {
      const next = getScreenClass()
      setScreen(next)
      if (next !== 'mobile') setDrawerOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const { mutate: logout } = useMutation({
    mutationFn: () =>
      refreshToken ? authService.logout({ refreshToken }, accessToken!) : Promise.resolve(),
    onSettled: () => {
      clearAuth()
      navigate('/login')
    },
    onError: () => toast({ variant: 'danger', title: 'Erro ao sair', message: 'Tente novamente.' }),
  })

  const navGroups = NAV_GROUPS.flatMap((group) => {
    const visibleItems = group.items
      .filter((item) => !item.permission || can(item.permission))
      .map((item) => ({
        ...item,
        onClick: () => {
          navigationGuard.attempt(() => {
            navigate(item.key)
            setDrawerOpen(false)
          })
        },
      }))
    return visibleItems.length > 0 ? [{ ...group, items: visibleItems }] : []
  })

  const footerItems = [
    {
      key: '/preferences',
      label: 'Preferências',
      icon: <Icon name="settings" />,
      onClick: () => {
        navigate('/preferences')
        setDrawerOpen(false)
      },
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: <Icon name="log-out" />,
      onClick: () => logout(),
    },
  ]

  const logo = (
    <span className="text-sm font-semibold text-primary-700 tracking-tight">
      {screen === 'mobile' || collapsed ? 'N' : 'Navega Plataforma'}
    </span>
  )

  const sharedSidebarProps = {
    groups: navGroups,
    activeKey: getActiveKey(pathname),
    logo,
    footerItems,
  }

  const isMobile = screen === 'mobile'
  const isTablet = screen === 'tablet'

  const drawerEl = (
    <SidebarDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} {...sharedSidebarProps} />
  )

  const persistentSidebar = isMobile ? undefined : (
    <Sidebar
      {...sharedSidebarProps}
      collapsed={isTablet ? true : collapsed}
      onCollapsedChange={isTablet ? undefined : setCollapsed}
    />
  )

  function handleMenuToggle() {
    if (isMobile) {
      setDrawerOpen((o) => !o)
    } else if (!isTablet) {
      setCollapsed((c) => !c)
    }
  }

  const sidebarCollapsed = isTablet ? true : collapsed

  useEffect(() => {
    const width = isMobile ? '0px' : sidebarCollapsed ? '4rem' : '15rem'
    document.documentElement.style.setProperty('--sidebar-width', width)
    return () => {
      document.documentElement.style.removeProperty('--sidebar-width')
    }
  }, [isMobile, sidebarCollapsed])

  return (
    <>
      {isMobile && drawerEl}
      <AppShell
        sidebarCollapsed={sidebarCollapsed}
        sidebar={persistentSidebar}
        topbar={
          <Topbar
            // O Topbar da DLS sempre renderiza o sino de notificações (sem prop para ocultar)
            className="[&_button:has(>.lucide-bell)]:hidden"
            breadcrumb={getBreadcrumbItems(pathname)}
            onMenuToggle={handleMenuToggle}
            searchSlot={null}
            rightSlot={
              <div className="flex items-center gap-2">
                <ExportsPopover />
                <button
                  type="button"
                  aria-label="Preferências"
                  title="Preferências"
                  onClick={() => navigationGuard.attempt(() => navigate('/preferences'))}
                  className="rounded-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                >
                  <Avatar name={user?.name ?? user?.email ?? 'Usuário'} size="sm" />
                </button>
              </div>
            }
          />
        }
      >
        <Outlet />
      </AppShell>
    </>
  )
}
